import { Model } from "../mvvm/Model.js";
const PLAN_RANK = {
    foundation: 0,
    payg: 1,
    starter: 2,
    growth: 3,
    pro: 4,
};
function planFloorLabel(rank) {
    if (rank >= PLAN_RANK.pro)
        return "Pro";
    if (rank >= PLAN_RANK.growth)
        return "Growth";
    if (rank >= PLAN_RANK.starter)
        return "Starter";
    if (rank >= PLAN_RANK.payg)
        return "Pay as you go";
    return "";
}
/** Effective hosting floor for an add-on (Custom + second location may use PAYG). */
export function addonMinPlanKey(addonKey, build, fallback) {
    if (addonKey === "second_kitchen" && build === "custom")
        return "payg";
    return fallback;
}
const SERVICES = [
    {
        key: "spark",
        name: "Spark",
        blurb: "Hours, location, and about — three pages. No online store.",
        buildRange: "$250–$400",
        includes: ["Hours, location, and about", "Three pages", "No online store"],
        excludes: ["Online ordering", "Owner menu tools"],
    },
    {
        key: "brand",
        name: "Brand",
        blurb: "Spark plus a PDF menu, contact page, and light motion. Still a brochure site.",
        buildRange: "$600–$900",
        includes: ["Everything in Spark", "PDF menu", "Contact page", "Light motion"],
        excludes: ["Online ordering", "Customer accounts"],
    },
    {
        key: "connect",
        name: "Connect",
        blurb: "Brand plus one live channel: online ordering, booking, or customer accounts. Includes full self-serve menu editing (prices, availability, items, and categories) after Lumora loads your starting menu.",
        buildRange: "$1,200–$2,000",
        recommended: true,
        includes: [
            "Everything in Brand",
            "One live channel (ordering, booking, or accounts)",
            "Full self-serve menu CRUD (create, rename, delete, price, and 86 items)",
        ],
        excludes: ["Multi-staff RBAC, loyalty, delivery, and kitchen screens (Commerce+)"],
    },
    {
        key: "commerce",
        name: "Commerce",
        blurb: "Online ordering plus growth and operations tools — loyalty, customer accounts, kitchen screens, and multi-staff roles.",
        buildRange: "$2,500–$4,000",
        includes: [
            "Online ordering",
            "Growth and ops tools (loyalty, kitchen screens, inventory, multi-staff RBAC)",
        ],
        excludes: ["Custom data shapes (Pro)", "Private dedicated hosting (Pro)"],
    },
    {
        key: "custom",
        name: "Custom",
        blurb: "Commerce plus extras built for you — inventory, scheduling, multi-admin, and more by quote.",
        buildRange: "$5,000+ (quote)",
        includes: ["Commerce as a baseline", "Extras scoped in a written quote"],
        excludes: ["A single fixed price for every project"],
    },
];
const BUILD_TIER_KEYS = ["spark", "brand", "connect", "commerce", "custom"];
const COMMERCE_BUILDS = ["commerce", "custom"];
const CONNECT_PLUS = ["connect", "commerce", "custom"];
/** Channel order for Commerce+: Online ordering → Booking → Customer accounts (#660). */
const ADDONS = [
    {
        key: "online_ordering",
        name: "Online ordering",
        minPlan: "Pay as you go+",
        minPlanKey: "payg",
        blurb: "Customers order on your site for pickup. Card checkout is included. Separate from delivery apps.",
        allowedBuilds: [...CONNECT_PLUS],
        exclusiveGroup: "connect_channel",
    },
    {
        key: "booking",
        name: "Booking",
        minPlan: "Pay as you go+",
        minPlanKey: "payg",
        blurb: "Appointments or reservations on your site. On Connect, pick this or ordering or accounts — not all three.",
        allowedBuilds: [...CONNECT_PLUS],
        exclusiveGroup: "connect_channel",
    },
    {
        key: "user_accounts",
        name: "Customer accounts",
        minPlan: "Growth",
        minPlanKey: "growth",
        blurb: "Customers sign in and keep a history with your business. Needs Growth hosting. On Connect, pick this or ordering or booking — not all three.",
        allowedBuilds: [...CONNECT_PLUS],
        exclusiveGroup: "connect_channel",
    },
    {
        key: "loyalty",
        name: "Loyalty",
        minPlan: "Growth",
        minPlanKey: "growth",
        blurb: "Points or punches for returning customers. Needs Growth hosting.",
        allowedBuilds: [...COMMERCE_BUILDS],
    },
    {
        key: "delivery",
        name: "Delivery",
        minPlan: "Growth",
        minPlanKey: "growth",
        blurb: "Your own delivery workflow (not DoorDash or Uber). Needs Growth hosting.",
        allowedBuilds: [...COMMERCE_BUILDS],
    },
    {
        key: "marketplace",
        name: "Marketplace apps",
        minPlan: "Growth",
        minPlanKey: "growth",
        blurb: "Bring DoorDash or Uber orders into your kitchen flow. Monthly connector fee — not our order cut. Needs partner approval.",
        allowedBuilds: [...COMMERCE_BUILDS],
    },
    {
        key: "signup_deal",
        name: "Signup deal",
        minPlan: "Growth",
        minPlanKey: "growth",
        blurb: "A one-time offer when someone creates an account. Needs Growth hosting.",
        allowedBuilds: [...COMMERCE_BUILDS],
    },
    {
        key: "wallet_pass",
        name: "Wallet pass",
        minPlan: "Growth",
        minPlanKey: "growth",
        blurb: "A pass in Apple Wallet or Google Wallet for loyalty. Needs Growth and the loyalty add-on.",
        allowedBuilds: [...COMMERCE_BUILDS],
    },
    {
        key: "kds",
        name: "Kitchen screen",
        minPlan: "Growth",
        minPlanKey: "growth",
        blurb: "A live order board for the kitchen. Needs Commerce (or Custom) and Growth hosting.",
        allowedBuilds: [...COMMERCE_BUILDS],
    },
    {
        key: "second_kitchen",
        name: "Second location",
        minPlan: "Growth",
        minPlanKey: "growth",
        blurb: "A second location with its own menu, hours, and printer. One-time setup fee. Connect needs Growth; Custom can use Pay as you go.",
        allowedBuilds: [...CONNECT_PLUS],
    },
    {
        key: "custom_tables",
        name: "Custom data",
        minPlan: "Pro",
        minPlanKey: "pro",
        blurb: "Information shapes we do not already support for every restaurant. Needs Pro hosting.",
        allowedBuilds: ["custom"],
    },
    {
        key: "vendor_auto_order",
        name: "Vendor auto-order",
        minPlan: "Pro",
        minPlanKey: "pro",
        blurb: "Draft purchase orders when stock runs low. Needs Pro hosting.",
        allowedBuilds: ["custom"],
    },
    {
        key: "private_container",
        name: "Private hosting",
        minPlan: "Pro",
        minPlanKey: "pro",
        blurb: "Your site runs on its own dedicated setup. Needs Pro hosting plus a separate quote.",
        allowedBuilds: ["custom"],
    },
];
const DISCLOSURES = [
    "Your content is yours. You can take the finished website with you.",
    "Your business data is yours. We can send you a copy on request.",
];
export const PHOTOGRAPHY_ADDON = {
    key: "photography",
    name: "Custom Brand Photography",
    blurb: "Need high-quality photos for your new site? We can shoot your menu, location, and team on site.",
};
/** Section intros for the public services page (plain language). */
export const CATALOG_SECTION_COPY = {
    build: "A one-time build of your website. Spark and Brand are simple brochure sites. Connect adds one live channel. Commerce lets you run the menu yourself. Custom is a quote for extras.",
    hosting: "A monthly (or yearly) plan that keeps the site online. Foundation is for brochure sites that need a custom domain and email — no Lumora API. Live stores need Pay as you go or higher.",
    addons: "Optional extras. What you can pick depends on the build you chose. Grey cards need a higher build. Picking an add-on can raise the minimum hosting plan below.",
    portability: "Your data and your content stay yours. You can host the finished website elsewhere, and you can ask us for a copy of your business records anytime.",
};
/** One-time website build tiers, add-ons, and portability lines (packages.md). */
export class ServiceCatalog extends Model {
    static services = SERVICES;
    static addons = ADDONS;
    static disclosures = DISCLOSURES;
    static sectionCopy = CATALOG_SECTION_COPY;
    static byKey(key) {
        const tier = this.services.find((s) => s.key === key);
        if (!tier)
            throw new Error(`Unknown service key: ${key}`);
        return tier;
    }
    static buildTiers() {
        return BUILD_TIER_KEYS.map((key) => ServiceCatalog.byKey(key));
    }
    static addonByKey(key) {
        const addon = this.addons.find((a) => a.key === key);
        if (!addon)
            throw new Error(`Unknown addon key: ${key}`);
        return addon;
    }
    /** Spark / Brand brochure sites use the static hosting ladder (no shared API). */
    static usesStaticHosting(key) {
        return key === "spark" || key === "brand";
    }
    /** Add-ons selectable for a build (not teasers). Spark and Brand get none. */
    static addonsForBuild(build) {
        if (!build || build === "spark" || build === "brand")
            return [];
        return ServiceCatalog.addons.filter((a) => a.allowedBuilds.includes(build));
    }
    /**
     * Cards to render on pricing: selectable add-ons plus higher-build teasers (greyed).
     */
    static marketingAddons(build) {
        if (!build || build === "spark" || build === "brand")
            return [];
        return ServiceCatalog.addons.filter((a) => {
            if (a.allowedBuilds.includes(build))
                return true;
            if (build === "connect") {
                return a.allowedBuilds.some((b) => b === "commerce" || b === "custom");
            }
            if (build === "commerce") {
                return a.allowedBuilds.includes("custom");
            }
            return false;
        });
    }
    /**
     * Why an add-on card is locked (build gate only), or null when selectable.
     * Hosting floor is enforced on the Hosting section — selecting a Pro add-on
     * must not require picking Pro hosting first (#pricing flow).
     * `host` is accepted for call-site compat and ignored for the lock decision.
     */
    static addonLockReason(key, build, _host) {
        if (!build)
            return "Choose a build first";
        const addon = ServiceCatalog.addonByKey(key);
        if (!addon.allowedBuilds.includes(build)) {
            if (addon.allowedBuilds.every((b) => b === "custom"))
                return "Needs Custom";
            if (addon.allowedBuilds.some((b) => b === "commerce" || b === "custom")) {
                return "Needs Commerce+";
            }
            return "Unavailable";
        }
        return null;
    }
    /** True when selected hosting is below this add-on's floor (used to prune). */
    static addonBelowHostFloor(key, build, host) {
        if (!host || host === "foundation")
            return false;
        const addon = ServiceCatalog.addonByKey(key);
        const min = addonMinPlanKey(key, build, addon.minPlanKey);
        return PLAN_RANK[host] < PLAN_RANK[min];
    }
    /** Soft label when current hosting is below the add-on floor (card stays clickable). */
    static addonHostFloorHint(key, build, host) {
        if (!ServiceCatalog.addonBelowHostFloor(key, build, host))
            return null;
        const addon = ServiceCatalog.addonByKey(key);
        const min = addonMinPlanKey(key, build, addon.minPlanKey);
        return `Needs ${planFloorLabel(PLAN_RANK[min])}+`;
    }
    /**
     * Drop add-ons that the build cannot select; on Connect keep one channel add-on.
     */
    static pruneAddonSelection(current, build) {
        const allowed = new Set(ServiceCatalog.addonsForBuild(build).map((a) => a.key));
        const next = new Set([...current].filter((k) => allowed.has(k)));
        if (build === "connect") {
            const channel = [...next].filter((k) => {
                try {
                    return ServiceCatalog.addonByKey(k).exclusiveGroup === "connect_channel";
                }
                catch {
                    return false;
                }
            });
            if (channel.length > 1) {
                const keep = ServiceCatalog.addons.find((a) => a.exclusiveGroup === "connect_channel" && channel.includes(a.key))?.key;
                for (const k of channel) {
                    if (k !== keep)
                        next.delete(k);
                }
            }
        }
        return next;
    }
    /** Drop add-ons that need a higher hosting plan than `host`. */
    static pruneAddonsForHost(current, build, host) {
        if (!host || host === "foundation")
            return new Set(current);
        const next = new Set();
        for (const key of current) {
            if (ServiceCatalog.addonLockReason(key, build, host))
                continue;
            if (ServiceCatalog.addonBelowHostFloor(key, build, host))
                continue;
            next.add(key);
        }
        return next;
    }
    /**
     * Toggle an add-on. Connect channel add-ons are exclusive (one of accounts / booking / ordering).
     */
    static toggleAddonSelection(current, key, build) {
        const allowed = new Set(ServiceCatalog.addonsForBuild(build).map((a) => a.key));
        if (!allowed.has(key))
            return new Set(current);
        const addon = ServiceCatalog.addonByKey(key);
        const next = new Set([...current].filter((k) => allowed.has(k)));
        if (next.has(key)) {
            next.delete(key);
            return next;
        }
        if (build === "connect" && addon.exclusiveGroup) {
            for (const other of ServiceCatalog.addons) {
                if (other.exclusiveGroup === addon.exclusiveGroup)
                    next.delete(other.key);
            }
        }
        next.add(key);
        return next;
    }
    validate() {
        const errors = [];
        if (ServiceCatalog.buildTiers().length !== 5) {
            errors.push("Build tiers must be Spark, Brand, Connect, Commerce, and Custom.");
        }
        if (ServiceCatalog.addonsForBuild("spark").length !== 0) {
            errors.push("Spark must offer no add-ons.");
        }
        if (ServiceCatalog.addonsForBuild("brand").length !== 0) {
            errors.push("Brand must offer no add-ons.");
        }
        if (!ServiceCatalog.addonsForBuild("connect").some((a) => a.key === "online_ordering")) {
            errors.push("Connect must offer online ordering.");
        }
        if (ServiceCatalog.addons.some((a) => a.key === "online_payments")) {
            errors.push("Online payments add-on must be removed (#660).");
        }
        if (ServiceCatalog.addonByKey("booking").minPlanKey !== "payg") {
            errors.push("Booking must be PAYG+.");
        }
        if (ServiceCatalog.addonByKey("second_kitchen").name !== "Second location") {
            errors.push("Second kitchen must be labeled Second location.");
        }
        if (ServiceCatalog.disclosures.length < 2)
            errors.push("Portability disclosures are missing.");
        return errors;
    }
}
/**
 * Canonical FE name (#1053): one-time website build catalog, distinct from the
 * hosting plan catalog and the backend service list. `ServiceCatalog` stays exported.
 */
export const BuildCatalog = ServiceCatalog;
//# sourceMappingURL=ServiceCatalog.js.map